import { supabase } from '../lib/supabase';

class PricingService {
  // Get all pricing tiers
  async getPricingTiers(activeOnly = true) {
    try {
      let query = supabase
        .from('pricing_tiers')
        .select('*')
        .order('size_min', { ascending: true });

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching pricing tiers:', error);
      throw error;
    }
  }

  // Get all pricing adjustments (add-ons)
  async getPricingAdjustments(activeOnly = true) {
    try {
      let query = supabase
        .from('pricing_adjustments')
        .select('*')
        .order('name', { ascending: true });

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // If no data found, return default values
      if (!data || data.length === 0) {
        console.warn('No pricing adjustments found, using defaults');
        return [
          { name: 'Linework', current_value: 450 },
          { name: 'Erosion Surface', current_value: 450 },
          { name: 'Advanced Utilities', current_value: 30 },
          { name: 'Utilities', current_value: 10 }
        ];
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching pricing adjustments:', error);
      // Return defaults on error
      return [
        { name: 'Linework', current_value: 450 },
        { name: 'Erosion Surface', current_value: 450 },
        { name: 'Advanced Utilities', current_value: 30 },
        { name: 'Utilities', current_value: 10 }
      ];
    }
  }

  // Update a pricing tier
  async updatePricingTier(id, updates, reason) {
    try {
      // First get the current values for history
      const { data: currentTier, error: fetchError } = await supabase
        .from('pricing_tiers')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Update the tier
      const { data, error } = await supabase
        .from('pricing_tiers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Log the changes to history
      await this.logPricingChange('pricing_tiers', id, currentTier, data, reason);

      return data;
    } catch (error) {
      console.error('Error updating pricing tier:', error);
      throw error;
    }
  }

  // Update a pricing adjustment
  async updatePricingAdjustment(id, updates, reason) {
    try {
      // First get the current values for history
      const { data: currentAdjustment, error: fetchError } = await supabase
        .from('pricing_adjustments')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Update the adjustment
      const { data, error } = await supabase
        .from('pricing_adjustments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Log the changes to history
      await this.logPricingChange('pricing_adjustments', id, currentAdjustment, data, reason);

      return data;
    } catch (error) {
      console.error('Error updating pricing adjustment:', error);
      throw error;
    }
  }

  // Apply selective pricing adjustments (new method for individual control)
  async applySelectivePriceAdjustments(adjustmentData) {
    try {
      const user = await this.getCurrentUser();
      const updates = [];
      // Handle acreage-based tier percentage increases
      if (adjustmentData.tiers && adjustmentData.tiers !== 0) {
        const increaseFactor = 1 + (adjustmentData.tiers / 100);
        // Get all pricing tiers
        const tiers = await this.getPricingTiers(false);
        
        for (const tier of tiers) {
          const oldData = { ...tier };
          const newData = { ...tier };
          
          // Apply percentage increase to monetary fields
          if (tier.base_amount) {
            newData.base_amount = Math.round(tier.base_amount * increaseFactor);
          }
          if (tier.per_acre_rate) {
            newData.per_acre_rate = Math.round(tier.per_acre_rate * increaseFactor);
          }
          if (tier.power_coefficient) {
            newData.power_coefficient = Math.round(tier.power_coefficient * increaseFactor);
          }
          if (tier.constant) {
            newData.constant = Math.round(tier.constant * increaseFactor);
          }
          
          // Update the tier
          const { error: updateError } = await supabase
            .from('pricing_tiers')
            .update({
              base_amount: newData.base_amount,
              per_acre_rate: newData.per_acre_rate,
              power_coefficient: newData.power_coefficient,
              constant: newData.constant,
              updated_at: new Date().toISOString()
            })
            .eq('id', tier.id);
            
          if (updateError) throw updateError;
          
          // Log the changes
          await this.logPricingChange('pricing_tiers', tier.id, oldData, newData, adjustmentData.reason);
          
          updates.push({
            table: 'pricing_tiers',
            id: tier.id,
            old_values: oldData,
            new_values: newData
          });
        }
      }
      
      // Handle individual adjustment updates
      const adjustments = await this.getPricingAdjustments(false);
      
      // Update Linework if specified and different from current value
      if (adjustmentData.linework !== null && adjustmentData.linework !== undefined) {
        const lineworkAdj = adjustments.find(a => a.name === 'Linework');
        if (lineworkAdj && lineworkAdj.id && Math.abs(lineworkAdj.current_value - adjustmentData.linework) > 0.01) {
          const oldData = { ...lineworkAdj };
          const { error: updateError } = await supabase
            .from('pricing_adjustments')
            .update({
              current_value: adjustmentData.linework,
              updated_at: new Date().toISOString()
            })
            .eq('id', lineworkAdj.id);
            
          if (updateError) throw updateError;
          
          const newData = { ...lineworkAdj, current_value: adjustmentData.linework };
          await this.logPricingChange('pricing_adjustments', lineworkAdj.id, oldData, newData, adjustmentData.reason);
          
          updates.push({
            table: 'pricing_adjustments',
            id: lineworkAdj.id,
            old_values: oldData,
            new_values: newData
          });
        } else if (lineworkAdj && lineworkAdj.id) {
          console.log('⚪ Linework unchanged:', lineworkAdj.current_value, '(matches target:', adjustmentData.linework, ')');
        } else {
          console.warn('⚠️ Linework adjustment record not found or missing ID, skipping update. Please ensure pricing_adjustments table is populated.');
        }
      }
      
      // Update Erosion Surface if specified and different from current value
      if (adjustmentData.erosion !== null && adjustmentData.erosion !== undefined) {
        const erosionAdj = adjustments.find(a => a.name === 'Erosion Surface');
        if (erosionAdj && erosionAdj.id && Math.abs(erosionAdj.current_value - adjustmentData.erosion) > 0.01) {
          const oldData = { ...erosionAdj };
          const { error: updateError } = await supabase
            .from('pricing_adjustments')
            .update({
              current_value: adjustmentData.erosion,
              updated_at: new Date().toISOString()
            })
            .eq('id', erosionAdj.id);
            
          if (updateError) throw updateError;
          
          const newData = { ...erosionAdj, current_value: adjustmentData.erosion };
          await this.logPricingChange('pricing_adjustments', erosionAdj.id, oldData, newData, adjustmentData.reason);
          
          updates.push({
            table: 'pricing_adjustments',
            id: erosionAdj.id,
            old_values: oldData,
            new_values: newData
          });
        } else if (erosionAdj && erosionAdj.id) {
          console.log('⚪ Erosion Surface unchanged:', erosionAdj.current_value, '(matches target:', adjustmentData.erosion, ')');
        } else {
          console.warn('⚠️ Erosion Surface adjustment record not found or missing ID, skipping update. Please ensure pricing_adjustments table is populated.');
        }
      }
      
      // Update Advanced Utilities if specified and different from current value
      if (adjustmentData.advUtility !== null && adjustmentData.advUtility !== undefined) {
        const advUtilityAdj = adjustments.find(a => a.name === 'Advanced Utilities');
        if (advUtilityAdj && advUtilityAdj.id && Math.abs(advUtilityAdj.current_value - adjustmentData.advUtility) > 0.01) {
          const oldData = { ...advUtilityAdj };
          const { error: updateError } = await supabase
            .from('pricing_adjustments')
            .update({
              current_value: adjustmentData.advUtility,
              updated_at: new Date().toISOString()
            })
            .eq('id', advUtilityAdj.id);
            
          if (updateError) throw updateError;
          
          const newData = { ...advUtilityAdj, current_value: adjustmentData.advUtility };
          await this.logPricingChange('pricing_adjustments', advUtilityAdj.id, oldData, newData, adjustmentData.reason);
          
          updates.push({
            table: 'pricing_adjustments',
            id: advUtilityAdj.id,
            old_values: oldData,
            new_values: newData
          });
        } else if (advUtilityAdj && advUtilityAdj.id) {
          console.log('⚪ Advanced Utilities unchanged:', advUtilityAdj.current_value, '(matches target:', adjustmentData.advUtility, ')');
        } else {
          console.warn('⚠️ Advanced Utilities adjustment record not found or missing ID, skipping update. Please ensure pricing_adjustments table is populated.');
        }
      }
      
      // Update Utilities percentage if specified and different from current value
      if (adjustmentData.utilityPercent !== null && adjustmentData.utilityPercent !== undefined) {
        const utilityAdj = adjustments.find(a => a.name === 'Utilities');
        if (utilityAdj && utilityAdj.id && Math.abs(utilityAdj.current_value - adjustmentData.utilityPercent) > 0.01) {
          const oldData = { ...utilityAdj };
          const { error: updateError } = await supabase
            .from('pricing_adjustments')
            .update({
              current_value: adjustmentData.utilityPercent,
              updated_at: new Date().toISOString()
            })
            .eq('id', utilityAdj.id);
            
          if (updateError) throw updateError;
          
          const newData = { ...utilityAdj, current_value: adjustmentData.utilityPercent };
          await this.logPricingChange('pricing_adjustments', utilityAdj.id, oldData, newData, adjustmentData.reason);
          
          updates.push({
            table: 'pricing_adjustments',
            id: utilityAdj.id,
            old_values: oldData,
            new_values: newData
          });
        } else if (utilityAdj && utilityAdj.id) {
          console.log('⚪ Utilities percentage unchanged:', utilityAdj.current_value, '(matches target:', adjustmentData.utilityPercent, ')');
        } else {
          console.warn('⚠️ Utilities percentage adjustment record not found or missing ID, skipping update. Please ensure pricing_adjustments table is populated.');
        }
      }
      
      // Changes already logged to pricing_history via logPricingChange
      return {
        success: true,
        updates_applied: updates.length,
        effective_date: adjustmentData.effectiveDate,
        updates: updates
      };
    } catch (error) {
      console.error('Error applying selective price adjustments:', error);
      throw error;
    }
  }

  // Apply percentage increase to all or selected items (legacy method)
  async applyPercentageIncrease(percentage, itemIds = null, compound = true, reason, effectiveDate = new Date()) {
    try {
      const user = await this.getCurrentUser();
      const increaseFactor = 1 + (percentage / 100);
      const updates = [];
      
      // Get all pricing tiers
      const tiers = await this.getPricingTiers(false); // Get all tiers, not just active ones
      
      // Apply increase to each tier
      for (const tier of tiers) {
        const oldData = { ...tier };
        const newData = { ...tier };
        
        // Apply percentage increase to monetary fields
        if (tier.base_amount) {
          newData.base_amount = Math.round(tier.base_amount * increaseFactor);
        }
        if (tier.per_acre_rate) {
          newData.per_acre_rate = Math.round(tier.per_acre_rate * increaseFactor);
        }
        if (tier.power_coefficient) {
          newData.power_coefficient = Math.round(tier.power_coefficient * increaseFactor);
        }
        if (tier.constant) {
          newData.constant = Math.round(tier.constant * increaseFactor);
        }
        
        // Update the tier
        const { error: updateError } = await supabase
          .from('pricing_tiers')
          .update({
            base_amount: newData.base_amount,
            per_acre_rate: newData.per_acre_rate,
            power_coefficient: newData.power_coefficient,
            constant: newData.constant,
            updated_at: new Date().toISOString()
          })
          .eq('id', tier.id);
          
        if (updateError) throw updateError;
        
        // Log the changes
        await this.logPricingChange('pricing_tiers', tier.id, oldData, newData, reason);
        
        updates.push({
          table: 'pricing_tiers',
          id: tier.id,
          old_values: oldData,
          new_values: newData
        });
      }
      
      // Get all pricing adjustments
      const adjustments = await this.getPricingAdjustments(false);
      
      // Apply increase to each adjustment
      for (const adjustment of adjustments) {
        const oldData = { ...adjustment };
        const newData = { ...adjustment };
        
        // Apply percentage increase to current_value
        if (adjustment.current_value) {
          newData.current_value = Math.round(adjustment.current_value * increaseFactor);
        }
        
        // Update the adjustment
        const { error: updateError } = await supabase
          .from('pricing_adjustments')
          .update({
            current_value: newData.current_value,
            updated_at: new Date().toISOString()
          })
          .eq('id', adjustment.id);
          
        if (updateError) throw updateError;
        
        // Log the changes
        await this.logPricingChange('pricing_adjustments', adjustment.id, oldData, newData, reason);
        
        updates.push({
          table: 'pricing_adjustments',
          id: adjustment.id,
          old_values: oldData,
          new_values: newData
        });
      }
      
      // Changes already logged to pricing_history via logPricingChange
      return {
        success: true,
        updates_applied: updates.length,
        percentage_increase: percentage,
        effective_date: effectiveDate,
        updates: updates
      };
    } catch (error) {
      console.error('Error applying percentage increase:', error);
      throw error;
    }
  }

  // Calculate pricing for a project
  async calculateProjectPrice(projectType, acres, options = {}) {
    try {
      // Validate acres doesn't exceed 150 for non-Linework projects
      if (projectType !== 'Linework' && acres > 150) {
        throw new Error('Projects larger than 150 acres require custom pricing. Please contact us for a quote.');
      }

      const {
        hasUtilities = false,
        hasErosion = false,
        hasAdvUtilities = false,
        advUtilityCount = 0
      } = options;

      // Special case for Linework
      if (projectType === 'Linework') {
        const { data: lineworkAdjustment } = await supabase
          .from('pricing_adjustments')
          .select('current_value')
          .eq('name', 'Linework')
          .eq('is_active', true)
          .single();

        return {
          base_cost: lineworkAdjustment?.current_value || 450,
          utility_cost: 0,
          erosion_cost: hasErosion ? await this.getAdjustmentValue('Erosion Surface') : 0,
          advanced_utility_cost: hasAdvUtilities ? (await this.getAdjustmentValue('Advanced Utilities') * advUtilityCount) : 0,
          total_cost: 0 // Will be calculated below
        };
      }

      // Find the appropriate tier
      const { data: tier } = await supabase
        .from('pricing_tiers')
        .select('*')
        .eq('project_type', projectType)
        .eq('is_active', true)
        .gte('size_max', acres)
        .lte('size_min', acres)
        .single();

      let selectedTier = tier;
      
      if (!tier) {
        // Try to find tier where acres is greater than size_min and size_max is null (for 5+ acres)
        const { data: largeTier } = await supabase
          .from('pricing_tiers')
          .select('*')
          .eq('project_type', projectType)
          .eq('is_active', true)
          .gte('size_min', acres)
          .is('size_max', null)
          .single();

        if (!largeTier) {
          throw new Error('No pricing tier found for this project configuration');
        }
        
        selectedTier = largeTier;
      }
      let baseCost = 0;

      // Calculate base cost based on formula type
      switch (selectedTier.formula_type) {
        case 'flat':
          baseCost = selectedTier.base_amount;
          break;
        case 'linear':
          baseCost = selectedTier.base_amount + (selectedTier.per_acre_rate * acres);
          break;
        case 'power':
          baseCost = selectedTier.power_coefficient * Math.pow(acres, selectedTier.power_exponent) + selectedTier.constant;
          break;
        case 'mixed':
          baseCost = selectedTier.base_amount + (selectedTier.per_acre_rate * acres);
          break;
        default:
          throw new Error(`Unknown formula type: ${selectedTier.formula_type}`);
      }

      // Round to nearest 10
      baseCost = Math.ceil(baseCost / 10) * 10;

      // Calculate add-ons
      const utilityCost = hasUtilities ? baseCost * (await this.getAdjustmentValue('Utilities') / 100) : 0;
      const erosionCost = hasErosion ? await this.getAdjustmentValue('Erosion Surface') : 0;
      const advUtilityCost = hasAdvUtilities ? (await this.getAdjustmentValue('Advanced Utilities') * advUtilityCount) : 0;

      return {
        base_cost: baseCost,
        utility_cost: Math.ceil(utilityCost / 10) * 10,
        erosion_cost: erosionCost,
        advanced_utility_cost: advUtilityCost,
        total_cost: baseCost + Math.ceil(utilityCost / 10) * 10 + erosionCost + advUtilityCost
      };
    } catch (error) {
      console.error('Error calculating project price:', error);
      throw error;
    }
  }

  // Get a specific adjustment value
  async getAdjustmentValue(name) {
    try {
      const { data, error } = await supabase
        .from('pricing_adjustments')
        .select('current_value')
        .eq('name', name)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data?.current_value || 0;
    } catch (error) {
      console.error(`Error fetching adjustment value for ${name}:`, error);
      return 0;
    }
  }

  // Get pricing history
  async getPricingHistory(filters = {}) {
    try {
      let query = supabase
        .from('pricing_history')
        .select('*')
        .order('changed_at', { ascending: false });

      if (filters.tableName) {
        query = query.eq('table_name', filters.tableName);
      }

      if (filters.recordId) {
        query = query.eq('record_id', filters.recordId);
      }

      if (filters.startDate) {
        query = query.gte('changed_at', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('changed_at', filters.endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching pricing history:', error);
      throw error;
    }
  }

  // Log pricing changes to history
  async logPricingChange(tableName, recordId, oldData, newData, reason) {
    try {
      const user = await this.getCurrentUser();
      const changes = [];

      // Compare old and new data to find changes
      for (const key in newData) {
        if (oldData[key] !== newData[key] && !['updated_at', 'created_at'].includes(key)) {
          changes.push({
            table_name: tableName,
            record_id: recordId,
            field_name: key,
            old_value: String(oldData[key]),
            new_value: String(newData[key]),
            change_type: 'update',
            changed_by: null, // employees table uses bigint id, not UUID - leave null
            change_reason: reason
          });
        }
      }

      if (changes.length > 0) {
        const { error } = await supabase
          .from('pricing_history')
          .insert(changes);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error logging pricing change:', error);
      // Don't throw here - we don't want to fail the main operation if logging fails
    }
  }

  // Get pricing at a specific date
  async getPricingAtDate(targetDate) {
    try {
      // This would be more complex in production - you'd need to reconstruct
      // the pricing state at a specific date by replaying all changes
      // For now, returning current pricing
      const tiers = await this.getPricingTiers();
      const adjustments = await this.getPricingAdjustments();
      
      return {
        tiers,
        adjustments,
        effectiveDate: targetDate
      };
    } catch (error) {
      console.error('Error fetching pricing at date:', error);
      throw error;
    }
  }

  // Export pricing history to CSV
  async exportPricingHistory(startDate, endDate) {
    try {
      const history = await this.getPricingHistory({ startDate, endDate });

      // Convert to CSV format
      const csvData = this.convertToCSV(history);
      return csvData;
    } catch (error) {
      console.error('Error exporting pricing history:', error);
      throw error;
    }
  }

  // Helper to convert data to CSV
  convertToCSV(data) {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value
      ).join(',')
    );

    return [headers, ...rows].join('\n');
  }

  // Calculate and update project calculated price
  async calculateAndUpdateProjectPrice(projectId) {
    try {
      // Get project data
      const { data: project, error: fetchError } = await supabase
        .from('projects')
        .select('approximate_acreage, additional_requests, project_type')
        .eq('id', projectId)
        .single();

      if (fetchError) throw fetchError;

      if (!project.approximate_acreage || project.approximate_acreage <= 0) {
        return null;
      }

      // Determine add-ons from additional_requests
      const additionalRequests = project.additional_requests || [];
      const hasUtilities = additionalRequests.some(req => 
        req.includes('3D/Elevated Pipe') || req.includes('Elevated Pipe')
      );
      const hasErosion = additionalRequests.some(req => 
        req.includes('Erosion Surface') || req.includes('Initial Erosion Surface')
      );

      // Calculate pricing
      const calculatedPrice = await this.calculateProjectPrice(
        'Typical Surface Model', // Default to surface model
        project.approximate_acreage,
        {
          hasUtilities,
          hasErosion,
          hasAdvUtilities: hasUtilities,
          advUtilityCount: hasUtilities ? 1 : 0
        }
      );

      // Update the project with calculated price
      const { error: updateError } = await supabase
        .from('projects')
        .update({ calculated_model_price: calculatedPrice.total_cost })
        .eq('id', projectId);

      if (updateError) throw updateError;

      return calculatedPrice.total_cost;
    } catch (error) {
      console.error('Error calculating and updating project price:', error);
      throw error;
    }
  }

  // Calculate price for project from project data
  async calculateProjectPriceFromData(projectData) {
    try {
      if (!projectData.approximate_acreage || projectData.approximate_acreage <= 0) {
        return null;
      }

      // Determine add-ons from additional_requests
      const additionalRequests = projectData.additional_requests || [];
      const hasUtilities = additionalRequests.some(req => 
        req.includes('3D/Elevated Pipe') || req.includes('Elevated Pipe')
      );
      const hasErosion = additionalRequests.some(req => 
        req.includes('Erosion Surface') || req.includes('Initial Erosion Surface')
      );

      // Calculate pricing
      const result = await this.calculateProjectPrice(
        'Typical Surface Model', // Default to surface model
        projectData.approximate_acreage,
        {
          hasUtilities,
          hasErosion,
          hasAdvUtilities: hasUtilities,
          advUtilityCount: hasUtilities ? 1 : 0
        }
      );

      return result;
    } catch (error) {
      console.error('Error calculating project price from data:', error);
      throw error;
    }
  }

  // Get current user
  async getCurrentUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: employee } = await supabase
        .from('employees')
        .select('*')
        .eq('email', user.email)
        .single();

      return employee;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }
}

export const pricingService = new PricingService();